// SWR Configuration with Error Recovery
// Provides resilient data fetching configuration for the application

import type { SWRConfiguration } from 'swr';
import { FetchError } from './resilient-fetch';

/**
 * Determines if an error should trigger a retry
 */
function shouldRetryOnError(error: Error): boolean {
  if (error instanceof FetchError) {
    // Don't retry on client errors (4xx)
    if (error.status && error.status >= 400 && error.status < 500) {
      return false;
    }
    // Retry on timeout, network errors, and server errors (5xx)
    if (error.isTimeout || error.isNetworkError) {
      return true;
    }
    if (error.status && error.status >= 500) {
      return true;
    }
  }
  // Default: retry on unknown errors
  return true;
}

/**
 * Custom error retry handler with exponential backoff
 */
function onErrorRetry(
  error: Error,
  key: string,
  config: SWRConfiguration,
  revalidate: (opts: { retryCount?: number }) => void,
  opts: { retryCount: number }
) {
  // Don't retry if we shouldn't based on error type
  if (!shouldRetryOnError(error)) {
    return;
  }

  // Maximum 3 retries
  if (opts.retryCount >= 3) {
    return;
  }

  // Exponential backoff: 1s, 2s, 4s
  const delay = 1000 * Math.pow(2, opts.retryCount);

  // Log retry attempt in development
  if (process.env.NODE_ENV === 'development') {
    console.warn(`SWR retry ${opts.retryCount + 1} for ${key} after ${delay}ms:`, error.message);
  }

  setTimeout(() => {
    revalidate({ retryCount: opts.retryCount + 1 });
  }, delay);
}

/**
 * Global SWR configuration with error recovery
 */
export const swrConfig: SWRConfiguration = {
  // Error handling
  onErrorRetry,
  shouldRetryOnError,

  // Revalidation settings
  revalidateOnFocus: false, // Don't revalidate when window gets focus
  revalidateOnReconnect: true, // Revalidate when network is restored
  revalidateIfStale: true, // Revalidate if data is stale

  // Cache settings
  dedupingInterval: 2000, // Dedupe requests within 2 seconds
  focusThrottleInterval: 5000, // Throttle focus revalidation to 5 seconds

  // Error retry configuration
  errorRetryInterval: 5000, // Wait 5 seconds between retries (overridden by onErrorRetry)
  errorRetryCount: 3, // Maximum 3 retries

  // Keep previous data while revalidating
  keepPreviousData: true,

  // Loading timeout
  loadingTimeout: 3000, // Consider request slow after 3 seconds

  // Success/Error callbacks
  onSuccess: (data, key) => {
    // Log successful data fetches in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`SWR success for ${key}`);
    }
  },

  onError: (error, key) => {
    // Log errors
    console.error(`SWR error for ${key}:`, error);

    // You could send errors to a monitoring service here
    // Example: sendToErrorTracking(error, key);
  },

  // Suspense mode (disabled by default)
  suspense: false,

  // Fallback data
  fallbackData: undefined,
};

/**
 * Create a custom SWR config for specific use cases
 */
export function createSWRConfig(overrides: Partial<SWRConfiguration> = {}): SWRConfiguration {
  return {
    ...swrConfig,
    ...overrides,
  };
}

/**
 * SWR config for real-time data (frequent updates)
 */
export const realtimeSWRConfig: SWRConfiguration = createSWRConfig({
  refreshInterval: 5000, // Refresh every 5 seconds
  revalidateOnFocus: true,
  dedupingInterval: 1000,
});

/**
 * SWR config for static data (infrequent updates)
 */
export const staticSWRConfig: SWRConfiguration = createSWRConfig({
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
  revalidateIfStale: false,
  dedupingInterval: 60000, // 1 minute
});

/**
 * SWR config for critical data (aggressive retries)
 */
export const criticalSWRConfig: SWRConfiguration = createSWRConfig({
  errorRetryCount: 5, // More retries for critical data
  revalidateOnFocus: true,
  revalidateOnReconnect: true,
});
